import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, RotateCcw, Sparkles } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";

function uid() {
  return (
    Date.now().toString(36) + Math.random().toString(36).slice(2, 10)
  );
}

export default function ChatWidget() {
  const { t, lang, dir } = useLang();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("bbm_chat_session") || uid());
  const [messages, setMessages] = useState([
    { role: "assistant", content: t.chat.greeting },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [unread, setUnread] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    localStorage.setItem("bbm_chat_session", sessionId);
  }, [sessionId]);

  // Update greeting when language changes (only if conversation is still fresh)
  useEffect(() => {
    setMessages((prev) =>
      prev.length === 1 && prev[0].role === "assistant"
        ? [{ role: "assistant", content: t.chat.greeting }]
        : prev
    );
  }, [t.chat.greeting]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Focus input on open
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 200);
      setUnread(false);
    }
  }, [open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setError("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const res = await api.post("/chat", {
        session_id: sessionId,
        message: msg,
        locale: lang,
      });
      setMessages((m) => [...m, { role: "assistant", content: res.data.reply }]);
      if (res.data.session_id) setSessionId(res.data.session_id);
      if (!open) setUnread(true);
    } catch (e) {
      setError(t.chat.error);
      setMessages((m) => [...m, { role: "assistant", content: t.chat.error, isError: true }]);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    const newId = uid();
    setSessionId(newId);
    localStorage.setItem("bbm_chat_session", newId);
    setMessages([{ role: "assistant", content: t.chat.greeting }]);
    setError("");
  };

  return (
    <>
      {/* Floating button */}
      <button
        data-testid="chat-fab"
        onClick={() => setOpen((v) => !v)}
        className={`fixed bottom-5 end-5 z-[60] flex items-center gap-2 px-4 py-3 rounded-full shadow-2xl transition-all ${
          open
            ? "bg-[#0B132B] text-white"
            : "bg-[#0047FF] text-white hover:bg-[#0036CC]"
        }`}
        aria-label={open ? t.chat.close : t.chat.open}
      >
        {open ? (
          <X className="w-5 h-5" />
        ) : (
          <>
            <MessageCircle className="w-5 h-5" />
            <span className="hidden sm:inline text-sm font-medium">{t.chat.open}</span>
            {unread && (
              <span className="w-2 h-2 rounded-full bg-[#FFB800] absolute top-2 end-2" />
            )}
          </>
        )}
      </button>

      {/* Chat panel */}
      {open && (
        <div
          data-testid="chat-panel"
          dir={dir}
          className="fixed bottom-20 end-5 z-[60] w-[calc(100vw-2.5rem)] sm:w-[400px] max-w-[400px] h-[600px] max-h-[calc(100vh-7rem)] bg-white border border-[#E5E7EB] rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="bg-[#0B132B] text-white px-4 py-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#0047FF] flex items-center justify-center shrink-0">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-base leading-tight" data-testid="chat-title">{t.chat.title}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-white/60 truncate">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-400 me-1.5 align-middle" />
                {t.chat.subtitle}
              </div>
            </div>
            <button
              onClick={reset}
              data-testid="chat-reset"
              title={t.chat.reset}
              className="text-white/60 hover:text-white p-1.5"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
            <button
              onClick={() => setOpen(false)}
              data-testid="chat-close"
              className="text-white/60 hover:text-white p-1.5"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto bg-[#F9FAFB] px-4 py-4 space-y-3"
            data-testid="chat-messages"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  data-testid={`chat-msg-${m.role}-${i}`}
                  className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words ${
                    m.role === "user"
                      ? "bg-[#0047FF] text-white rounded-br-sm"
                      : m.isError
                      ? "bg-red-50 text-red-700 border border-red-200 rounded-bl-sm"
                      : "bg-white text-[#0B132B] border border-[#E5E7EB] rounded-bl-sm"
                  }`}
                  style={
                    m.role === "user" && dir === "rtl"
                      ? { borderBottomRightRadius: "1rem", borderBottomLeftRadius: "0.25rem" }
                      : undefined
                  }
                >
                  {m.content}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start" data-testid="chat-typing">
                <div className="bg-white border border-[#E5E7EB] rounded-2xl rounded-bl-sm px-3.5 py-2.5">
                  <div className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-[#4B5563] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1.5 h-1.5 bg-[#4B5563] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-1.5 h-1.5 bg-[#4B5563] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}

            {/* Suggestions (only on first interaction) */}
            {messages.length === 1 && !loading && (
              <div className="pt-2 flex flex-wrap gap-2" data-testid="chat-suggestions">
                {t.chat.suggestions.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => send(s)}
                    data-testid={`chat-suggestion-${i}`}
                    className="px-3 py-1.5 text-xs bg-white border border-[#E5E7EB] hover:border-[#0047FF] hover:text-[#0047FF] text-[#0B132B] rounded-full transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="border-t border-[#E5E7EB] p-3 bg-white"
            data-testid="chat-form"
          >
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder={t.chat.placeholder}
                disabled={loading}
                data-testid="chat-input"
                className="flex-1 resize-none px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#0047FF] focus:ring-2 focus:ring-[#0047FF]/20 max-h-24"
                style={{ minHeight: "40px" }}
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                data-testid="chat-send"
                className="w-10 h-10 rounded-lg bg-[#0047FF] text-white flex items-center justify-center hover:bg-[#0036CC] disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
                aria-label={t.chat.send}
              >
                <Send className="w-4 h-4 bbm-flip" />
              </button>
            </div>
            <div className="mt-2 text-center font-mono text-[10px] uppercase tracking-widest text-[#4B5563]">
              {t.chat.poweredBy}
            </div>
          </form>
        </div>
      )}
    </>
  );
}
